import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';
import { useTheme, useBrand } from '../context/ColorContext';
import { Mail, Phone, MapPin, Clock, Send, Instagram, Facebook, Linkedin, Youtube, Loader2 } from 'lucide-react';

const DEFAULT_ILETISIM = {
    heroTitle: "İletişim",
    heroSubtitle: "Sorularınız, önerileriniz veya işbirlikleriniz için bizimle iletişime geçin.",
    locations: [
        {
            name: "Tuzla Yerleşkesi",
            address: "Kimya San.Org.San.Bölg.Tem Yan Yol Cd.N.4 Melek Aras Blv.N.48 Tuzla İstanbul Türkiye",
            phone: "+90 216 593 07 27, +90 216 593 18 50",
            email: "infokayalar@kayalarkimya.com.tr",
            workHours: "Pzt - Cuma: 08:30 - 18:00",
            mapEmbedUrl: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1508.4312891979927!2d29.350811210287515!3d40.87440306354148!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14cad1ab86b51c85%3A0xe7f9b87b7deedbd4!2sKayalar%2BKimya!5e0!3m2!1str!2str!4v1714000000000!5m2!1str!2str"
        },
        {
            name: "Gebze Yerleşkesi",
            address: "İhsan Dede Cad. 800 Sok. No:143/1 Gebze 41400 Kocaeli Türkiye",
            phone: "+90 262 673 76 60",
            email: "infokayalar@kayalarkimya.com.tr",
            workHours: "Pzt - Cuma: 08:30 - 18:00",
            mapEmbedUrl: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3019.243575510656!2d29.44474771512!3d40.82276533878144!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14cade820e1761d7%3A0x8663806fb27b6863!2sKayalar%20Kimya%20Gebze!5e0!3m2!1str!2str!4v1714000000000!5m2!1str!2str"
        }
    ]
};

const Iletisim = () => {
    const { theme } = useTheme();
    const { activeBrand } = useBrand();
    const isDark = theme === 'dark';

    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        subject: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { data: pageData = DEFAULT_ILETISIM, isLoading } = useQuery({
        queryKey: ['page', 'iletisim'],
        queryFn: async () => {
            try {
                const res = await axios.get('/api/pages/iletisim');
                return res.data;
            } catch (error) {
                return DEFAULT_ILETISIM;
            }
        },
        staleTime: 1000 * 60 * 5, // 5 dakika cache
    });



    const mascotImages = {
        'dufa': '/img/dufa-kirpi-transparan.png',
        'genc': '/img/genc-maskot.png',
        'woodsol': '/img/woodsol-maskot.png'
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if(!formData.fullName || !formData.email || !formData.message) {
            toast.error("Lütfen zorunlu alanları (Ad Soyad, E-Posta, Mesaj) doldurunuz.");
            return;
        }
        setIsSubmitting(true);
        try {
            await axios.post('/api/mail/contact', formData);
            toast.success('Mesajınız başarıyla gönderildi.');
            setFormData({ fullName: '', email: '', subject: '', message: '' });
        } catch (error) {
            toast.error(error.response?.data?.error || 'Mesaj gönderilirken bir hata oluştu.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className={`min-h-screen flex items-center justify-center pt-32 pb-20 ${isDark ? 'bg-black text-white' : 'bg-white text-slate-900'}`}>
                <Loader2 size={48} className="animate-spin opacity-20" />
            </div>
        );
    }

    return (
        <div className={`min-h-screen pt-32 pb-20 relative ${isDark ? 'text-white' : 'text-slate-900'}`}>
            <div className="container mx-auto px-4 md:px-0 relative z-10">
                {/* Header */}
                <div className="text-center mb-20 md:mb-32">
                    <span className="text-sm font-bold tracking-[0.4em] opacity-60 uppercase mb-4 block" style={{ color: activeBrand.primaryColor }}>
                        Bize Ulaşın
                    </span>
                    <h1 className="text-5xl md:text-8xl font-black uppercase mb-8 tracking-tighter leading-none">
                        {pageData.heroTitle}
                    </h1>
                    <p className="max-w-2xl mx-auto text-lg md:text-xl font-light opacity-70 leading-relaxed">
                        {pageData.heroSubtitle}
                    </p>
                </div>

                <div className="space-y-16">
                    {/* LOCATIONS GRID: Tuzla Left, Gebze Right */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-16">
                        {(pageData.locations || DEFAULT_ILETISIM.locations).map((loc, idx) => (
                            <div key={idx} className="flex flex-col h-full">
                                <h3 className="text-2xl font-black uppercase tracking-tight flex items-center gap-3 mb-6">
                                    <div className="w-2 h-8 rounded-full" style={{ backgroundColor: activeBrand.primaryColor }}></div>
                                    {loc.name || `Yerleşke ${idx + 1}`}
                                </h3>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                    {/* Address & Work Hours */}
                                    <div className={`p-8 rounded-[2rem] border ${isDark ? 'bg-white/5 border-white/10' : 'bg-black/5 border-black/5'} transition-all`} onMouseEnter={(e) => e.currentTarget.style.borderColor = activeBrand.primaryColor} onMouseLeave={(e) => e.currentTarget.style.borderColor = ''}>
                                        <div className="mb-4" style={{ color: activeBrand.primaryColor }}><MapPin size={24} /></div>
                                        <h4 className="text-sm font-black uppercase tracking-widest mb-2">Adres</h4>
                                        <p className="text-base font-light opacity-80 leading-relaxed mb-6">
                                            {loc.address}
                                        </p>
                                        
                                        {loc.workHours && (
                                            <div>
                                                <div className="mb-2" style={{ color: activeBrand.primaryColor }}><Clock size={20} /></div>
                                                <h4 className="text-xs font-bold uppercase tracking-widest opacity-60 mb-1">Çalışma Saatleri</h4>
                                                <p className="text-sm font-light opacity-80">{loc.workHours}</p>
                                            </div>
                                        )}
                                    </div>

                                    {/* Phones & Email */}
                                    <div className="flex flex-col gap-6 h-full">
                                        <div className={`p-8 rounded-[2rem] border ${isDark ? 'bg-white/5 border-white/10' : 'bg-black/5 border-black/5'} transition-all flex flex-col justify-center flex-1`} onMouseEnter={(e) => e.currentTarget.style.borderColor = activeBrand.primaryColor} onMouseLeave={(e) => e.currentTarget.style.borderColor = ''}>
                                            <div className="mb-4" style={{ color: activeBrand.primaryColor }}><Phone size={24} /></div>
                                            <h4 className="text-sm font-black uppercase tracking-widest mb-3">Telefon</h4>
                                            <div className="space-y-2">
                                                {loc.phone?.split(',').map((p, i) => (
                                                    <a key={i} href={`tel:${p.replace(/\s+/g, '')}`} className="text-base font-light opacity-80 hover:opacity-100 transition-opacity block">
                                                        {p.trim()}
                                                    </a>
                                                ))}
                                            </div>
                                        </div>

                                        <div className={`p-8 rounded-[2rem] border ${isDark ? 'bg-white/5 border-white/10' : 'bg-black/5 border-black/5'} transition-all flex flex-col justify-center flex-1`} onMouseEnter={(e) => e.currentTarget.style.borderColor = activeBrand.primaryColor} onMouseLeave={(e) => e.currentTarget.style.borderColor = ''}>
                                            <div className="mb-4" style={{ color: activeBrand.primaryColor }}><Mail size={24} /></div>
                                            <h4 className="text-sm font-black uppercase tracking-widest mb-2">E-Posta</h4>
                                            <a href={`mailto:${loc.email}`} className="text-base font-light opacity-80 hover:opacity-100 transition-opacity block">
                                                {loc.email}
                                            </a>
                                        </div>
                                    </div>
                                </div>

                                {/* Map */}
                                <div className="relative h-72 rounded-[2rem] overflow-hidden group border border-black/5 dark:border-white/10 mt-auto shadow-sm">
                                    {loc.mapEmbedUrl ? (
                                        <iframe 
                                            src={loc.mapEmbedUrl} 
                                            className="w-full h-full border-0 grayscale hover:grayscale-0 transition-all duration-700"
                                            allowFullScreen="" 
                                            loading="lazy" 
                                            referrerPolicy="no-referrer-when-downgrade"
                                        ></iframe>
                                    ) : (
                                        <>
                                            <div className="absolute inset-0 z-10 flex items-center justify-center backdrop-blur-sm" style={{ backgroundColor: `${activeBrand.primaryColor}20` }}>
                                                <div className="text-center p-6 relative z-20">
                                                    <h4 className="font-black uppercase tracking-widest mb-2">Konum Haritası Eklenmedi</h4>
                                                </div>
                                            </div>
                                            <img src="https://images.unsplash.com/photo-1524661135-423995f22d0b?auto=format&fit=crop&q=80&w=800" className="w-full h-full object-cover grayscale opacity-50" alt="Map Placeholder" />
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* CONTACT FORM & SOCIALS */}
                    <div className="max-w-4xl mx-auto space-y-16">
                        {/* Contact Form */}
                        <div className={`p-8 md:p-12 rounded-[3rem] border ${isDark ? 'bg-white/5 border-white/10' : 'bg-black/5 border-black/5'} backdrop-blur-3xl`}>
                            <h2 className="text-3xl font-black uppercase mb-8 tracking-tight text-center">Mesaj Gönderin</h2>
                            <form className="space-y-6" onSubmit={handleSubmit}>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold uppercase tracking-widest opacity-50 ml-4">Ad Soyad *</label>
                                        <input
                                            type="text"
                                            value={formData.fullName}
                                            onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                                            className={`w-full px-6 py-4 rounded-2xl outline-none border transition-all ${isDark ? 'bg-black/20 border-white/10' : 'bg-white border-black/5'}`}
                                            style={{ focusBorderColor: activeBrand.primaryColor }}
                                            placeholder="Adınız Soyadınız"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold uppercase tracking-widest opacity-50 ml-4">E-Posta *</label>
                                        <input
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                                            className={`w-full px-6 py-4 rounded-2xl outline-none border transition-all ${isDark ? 'bg-black/20 border-white/10' : 'bg-white border-black/5'}`}
                                            placeholder="e-posta@adresiniz.com"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold uppercase tracking-widest opacity-50 ml-4">Konu</label>
                                    <input
                                        type="text"
                                        value={formData.subject}
                                        onChange={(e) => setFormData({...formData, subject: e.target.value})}
                                        className={`w-full px-6 py-4 rounded-2xl outline-none border transition-all ${isDark ? 'bg-black/20 border-white/10' : 'bg-white border-black/5'}`}
                                        placeholder="Nasıl yardımcı olabiliriz?"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold uppercase tracking-widest opacity-50 ml-4">Mesajınız *</label>
                                    <textarea
                                        rows="5"
                                        value={formData.message}
                                        onChange={(e) => setFormData({...formData, message: e.target.value})}
                                        className={`w-full px-6 py-4 rounded-2xl outline-none border transition-all resize-none ${isDark ? 'bg-black/20 border-white/10' : 'bg-white border-black/5'}`}
                                        placeholder="Mesajınızı buraya yazın..."
                                    ></textarea>
                                </div>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className={`w-full py-5 text-white rounded-2xl font-black uppercase tracking-widest transition-all shadow-xl flex items-center justify-center gap-3 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:scale-[1.02] active:scale-[0.98]'}`}
                                    style={{ backgroundColor: activeBrand.primaryColor, boxShadow: `0 10px 15px -3px ${activeBrand.primaryColor}40` }}
                                >
                                    {isSubmitting ? <Loader2 size={20} className="animate-spin" /> : <Send size={20} />}
                                    {isSubmitting ? 'Gönderiliyor...' : 'Mesajı Gönder'}
                                </button>
                            </form>
                        </div>

                        {/* Social Media */}
                        <div className={`p-10 rounded-[2rem] border`} style={{ backgroundColor: `${activeBrand.primaryColor}10`, borderColor: `${activeBrand.primaryColor}20` }}>
                            <h3 className="text-xl font-black uppercase mb-6 tracking-tight text-center">Sosyal Medyada Biz</h3>
                            <div className="flex flex-wrap justify-center gap-6">
                                {[
                                    { icon: <Instagram />, link: '#', color: 'hover:bg-gradient-to-tr from-[#f9ce34] via-[#ee2a7b] to-[#6228d7]' },
                                    { icon: <Facebook />, link: '#', color: 'hover:bg-[#1877F2]' },
                                    { icon: <Linkedin />, link: '#', color: 'hover:bg-[#0A66C2]' },
                                    { icon: <Youtube />, link: '#', color: 'hover:bg-[#FF0000]' },
                                ].map((social, idx) => (
                                    <a
                                        key={idx}
                                        href={social.link}
                                        className={`w-14 h-14 rounded-2xl border ${isDark ? 'border-white/10 bg-white/5' : 'border-black/5 bg-black/5'} flex items-center justify-center transition-all hover:scale-110 hover:text-white ${social.color}`}
                                    >
                                        {social.icon}
                                    </a>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Iletisim;
